import React, { useMemo, useState } from 'react';
import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  Col,
  Empty,
  Modal,
  Row,
  Segmented,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  ExperimentOutlined,
  PlayCircleOutlined,
  TrophyOutlined,
  BulbOutlined,
  ThunderboltOutlined,
  AppstoreOutlined,
  ReadOutlined,
} from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import MainLayout from '../../components/common/MainLayout';
import { canManageUsers } from '../../types/permissions';
import type { User } from '../../types/permissions';
import { useListScenariosQuery, useStartScenarioMutation } from '../../api/savesV2Api';
import type { ScenarioKind, ScenarioTemplate } from '../../types/saves';

const { Title, Paragraph, Text } = Typography;

// Подписи и подсказки: saves:gallery.kinds.<kind>.label / .hint
const KIND_META: Record<ScenarioKind, { color: string; icon: React.ReactNode }> = {
  tutorial: { color: 'cyan', icon: <ReadOutlined /> },
  mechanical: { color: 'blue', icon: <ThunderboltOutlined /> },
  explanatory: { color: 'green', icon: <BulbOutlined /> },
  competitive: { color: 'gold', icon: <TrophyOutlined /> },
  campaign: { color: 'purple', icon: <AppstoreOutlined /> },
};

type KindFilter = 'all' | ScenarioKind;

const ScenarioCard: React.FC<{
  template: ScenarioTemplate;
  onStart: (template: ScenarioTemplate) => void;
  starting: boolean;
}> = ({ template, onStart, starting }) => {
  const { t } = useTranslation('saves');
  const known = KIND_META[template.kind];
  const meta = known
    ? {
      ...known,
      label: t(`gallery.kinds.${template.kind}.label`),
      hint: t(`gallery.kinds.${template.kind}.hint`),
    }
    : {
      label: template.kind,
      color: 'default',
      icon: null,
      hint: '',
    };
  const goalsCount = template.goals?.length ?? 0;

  return (
    <Card
      style={{ height: '100%' }}
      title={
        <Space>
          {meta.icon}
          <Text strong>{template.title || template.slug}</Text>
        </Space>
      }
      extra={
        <Tooltip title={meta.hint}>
          <Tag color={meta.color}>{meta.label}</Tag>
        </Tooltip>
      }
      actions={[
        <Button
          type="primary"
          icon={<PlayCircleOutlined />}
          loading={starting}
          onClick={() => onStart(template)}
        >
          {t('gallery.start')}
        </Button>,
      ]}
    >
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        {template.narrative && typeof (template.narrative as Record<string, unknown>).intro === 'string' ? (
          <Paragraph ellipsis={{ rows: 3 }} style={{ marginBottom: 0 }}>
            {String((template.narrative as Record<string, unknown>).intro)}
          </Paragraph>
        ) : (
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            {t('gallery.noDescription')}
          </Paragraph>
        )}

        <div>
          <Text type="secondary">{t('gallery.duration')} </Text>
          <Text strong>{t('gallery.days', { count: template.durationDays })}</Text>
        </div>
        <div>
          <Text type="secondary">{t('gallery.goals')} </Text>
          <Text strong>{goalsCount}</Text>
        </div>
        <div>
          <Text type="secondary">{t('gallery.visibility')} </Text>
          <Tag>{template.visibility}</Tag>
        </div>
        <div>
          <Text type="secondary">Slug: </Text>
          <Text code>{template.slug}</Text>
        </div>
        <div>
          <Text type="secondary">{t('gallery.version')} </Text>
          <Text>v{template.version}</Text>
        </div>
      </Space>
    </Card>
  );
};

export default function ScenariosGalleryPage() {
  const { t } = useTranslation('saves');
  const navigate = useNavigate();
  const [kindFilter, setKindFilter] = useState<KindFilter>('all');
  const user = useSelector((state: { auth: { user: User | null } }) => state.auth.user);
  const isAdmin = canManageUsers(user);

  const queryArgs = useMemo(() => {
    return kindFilter === 'all' ? {} : { kind: kindFilter };
  }, [kindFilter]);

  const { data: scenarios, isLoading, isError, refetch } = useListScenariosQuery(queryArgs);
  const [startScenario, { isLoading: starting }] = useStartScenarioMutation();
  const [pendingTemplateId, setPendingTemplateId] = useState<string | null>(null);

  const handleStart = (template: ScenarioTemplate) => {
    Modal.confirm({
      title: t('gallery.confirmStart.title', { name: template.title || template.slug }),
      content: (
        <Space direction="vertical">
          <Text>
            <Trans t={t} i18nKey="gallery.confirmStart.body" components={{ tag: <Tag color="orange" /> }} />
          </Text>
          <Text type="secondary">
            {t('gallery.confirmStart.meta', {
              days: t('gallery.days', { count: template.durationDays }),
              version: template.version,
            })}
          </Text>
        </Space>
      ),
      okText: t('gallery.start'),
      cancelText: t('common:actions.cancel'),
      onOk: async () => {
        try {
          setPendingTemplateId(template.scenarioTemplateId);
          const result = await startScenario({
            templateId: template.scenarioTemplateId,
          }).unwrap();
          message.success(t('gallery.started'));
          if (result?.save?.id) {
            navigate(`/saves/${result.save.id}`);
          }
        } catch (err) {
          const e = err as { data?: { message?: string } };
          message.error(e?.data?.message || t('gallery.startFailed'));
        } finally {
          setPendingTemplateId(null);
        }
      },
    });
  };

  return (
    <MainLayout
      breadcrumb={
        <Breadcrumb
          items={[
            { title: <Link to="/">{t('shared.breadcrumbHome')}</Link> },
            { title: <Link to="/saves">{t('shared.breadcrumbSaves')}</Link> },
            { title: t('gallery.title') },
          ]}
        />
      }
    >
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
        <Title level={3} style={{ margin: 0 }}>
          <ExperimentOutlined /> {t('gallery.title')}
        </Title>
        <Space wrap>
          <Button icon={<PlayCircleOutlined />} onClick={() => navigate('/saves')}>
            {t('gallery.gameSaves')}
          </Button>
          <Button icon={<ThunderboltOutlined />} onClick={() => navigate('/saves?type=free')}>
            {t('gallery.freePlay')}
          </Button>
          <Button onClick={() => refetch()}>{t('common:actions.refresh')}</Button>
        </Space>
      </Space>

      <Space wrap style={{ marginBottom: 16 }}>
        <Text type="secondary">{t('gallery.kindFilter')}</Text>
        <Segmented
          value={kindFilter}
          onChange={(v) => setKindFilter(v as KindFilter)}
          options={[
            { label: t('common:states.all'), value: 'all' },
            { label: t('gallery.kinds.tutorial.filter'), value: 'tutorial' },
            { label: t('gallery.kinds.mechanical.filter'), value: 'mechanical' },
            { label: t('gallery.kinds.explanatory.filter'), value: 'explanatory' },
            { label: t('gallery.kinds.competitive.filter'), value: 'competitive' },
            { label: t('gallery.kinds.campaign.filter'), value: 'campaign' },
          ]}
        />
      </Space>

      {isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('gallery.loadFailed')}
        />
      )}

      {isAdmin && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('gallery.adminInfo.title')}
          description={t('gallery.adminInfo.description')}
          action={(
            <Button size="small" onClick={() => navigate('/admin/scenarios')}>
              {t('gallery.adminInfo.compareWithSeeds')}
            </Button>
          )}
        />
      )}

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 50 }}>
          <Spin size="large" />
        </div>
      ) : !scenarios || scenarios.length === 0 ? (
        <Empty description={t('gallery.empty')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <Row gutter={[16, 16]}>
          {scenarios.map((tpl) => (
            <Col key={`${tpl.scenarioTemplateId}-${tpl.version}`} xs={24} md={12} xl={8}>
              <ScenarioCard
                template={tpl}
                onStart={handleStart}
                starting={starting && pendingTemplateId === tpl.scenarioTemplateId}
              />
            </Col>
          ))}
        </Row>
      )}
    </MainLayout>
  );
}
